import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/api/client";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer,
} from "recharts";
import { ArrowLeft, ArrowRight, Scale, FileDown, CheckCircle2, Check, Loader2, UserCog, BarChart3, Users, Share2, Target, Lightbulb, MessageCircleQuestion } from "lucide-react";
import { AutosaveBanner } from "@/components/AutosaveBanner";

// ---------------------------------------------------------------------------
// Contenido EXACTO del instrumento (idéntico a index.html). No modificar
// preguntas, alternativas, orden, dimensiones, puntajes ni ítems inversos.
// ---------------------------------------------------------------------------
const SURVEY_DIMENSIONS = [
  { id: "D1", code: "D1", title: "D1. Rol, Autonomía y Delegación", desc: "Capacidad para distribuir tareas con claridad y delegar oportunamente.", questions: [
    { id: "B1", text: "Antes de asumir personalmente una tarea urgente, evalúo si corresponde delegarla.", inv: false },
    { id: "B2", text: "Entrego instrucciones claras sobre el resultado esperado y el margen de autonomía.", inv: false },
    { id: "B3", text: "Termino haciendo tareas operativas porque es más rápido que explicarlas.", inv: true },
  ]},
  { id: "D2", code: "D2", title: "D2. Autorregulación y Bienestar", desc: "Gestión proactiva del estrés y trato respetuoso bajo presión.", questions: [
    { id: "B4", text: "Identifico mis señales físicas y emocionales de sobrecarga.", inv: false },
    { id: "B5", text: "Mantengo la calma frente a situaciones de alta presión.", inv: false },
    { id: "B6", text: "Establezco límites claros entre mi jornada laboral y mi tiempo personal.", inv: false },
  ]},
  { id: "D3", code: "D3", title: "D3. Conflictos y Conversaciones", desc: "Abordaje oportuno de discrepancias y retroalimentación constructiva.", questions: [
    { id: "B7", text: "Abordo los desacuerdos en el equipo de manera oportuna.", inv: false },
    { id: "B8", text: "Preparo las conversaciones difíciles definiendo el objetivo y los hechos.", inv: false },
    { id: "B9", text: "Evito dar retroalimentación correctiva por temor a generar malestar.", inv: true },
  ]},
  { id: "D4", code: "D4", title: "D4. Supervisión y Equipos Híbridos", desc: "Establecimiento de metas verificables y confianza a distancia.", questions: [
    { id: "B10", text: "Establezco metas verificables para el trabajo a distancia.", inv: false },
    { id: "B11", text: "Mantengo canales de comunicación y coordinación regulares.", inv: false },
    { id: "B12", text: "Promuevo la confianza en la modalidad híbrida evitando el control excesivo.", inv: false },
  ]},
  { id: "D5", code: "D5", title: "D5. Planificación y Alineamiento", desc: "Traducción de prioridades estratégicas y seguimiento periódico.", questions: [
    { id: "B13", text: "Traduzco las prioridades institucionales en planes comprensibles.", inv: false },
    { id: "B14", text: "Reviso periódicamente el avance de las metas y reasigno prioridades.", inv: false },
    { id: "B15", text: "Comunico el sentido e impacto del trabajo en los objetivos institucionales.", inv: false },
  ]},
  { id: "D6", code: "D6", title: "D6. Gestión del Desempeño", desc: "Acompañamiento individual y acuerdos de mejora claros.", questions: [
    { id: "B16", text: "Realizo reuniones periódicas de seguimiento individual.", inv: false },
    { id: "B17", text: "Reconozco explícitamente los logros individuales y colectivos.", inv: false },
    { id: "B18", text: "Cuando el desempeño no es el esperado, acuerdo planes de mejora.", inv: false },
  ]},
];

const CASES_DATA = [
  { id: "C1", dim: "D1", ctx: "Se acerca la fecha límite de un informe crítico. Un profesional de su equipo, habitualmente autónomo, le avisa que está atascado y no cree lograrlo.", opts: [
    { txt: "Me reúno brevemente para identificar el obstáculo, ajustamos el alcance juntos y mantengo su responsabilidad.", pts: 3, l: "A" },
    { txt: "Extiendo el plazo de entrega para reducir su presión, pidiéndole que me avise si requiere ayuda más adelante.", pts: 2, l: "B" },
    { txt: "Le indico exactamente los pasos que debe seguir para terminar hoy, revisando yo mismo el documento final.", pts: 1, l: "C" },
    { txt: "Asumo la redacción final del documento para asegurar la calidad y cumplir con el plazo institucional exigido.", pts: 0, l: "D" },
  ]},
  { id: "C2", dim: "D2", ctx: "Son las 17:00 hrs de un viernes. Llega un requerimiento urgente de la subdirección. Su equipo está agotado tras una semana de alta carga.", opts: [
    { txt: "Exijo al equipo que se quede trabajando hasta terminar, asumiendo que el compromiso institucional lo requiere.", pts: 0, l: "A" },
    { txt: "Analizo el requerimiento para separar lo críticamente urgente de lo postergable, comunicando un plan focalizado.", pts: 3, l: "B" },
    { txt: "Asumo la carga de trabajo yo solo para no afectar la jornada de mi equipo, quedándome hasta resolverlo.", pts: 2, l: "C" },
    { txt: "Solicito formalmente una extensión de plazo a la dirección, argumentando que el equipo ya cumplió su horario.", pts: 1, l: "D" },
  ]},
  { id: "C3", dim: "D3", ctx: "Dos profesionales clave discuten frecuentemente sobre cómo ejecutar un procedimiento, generando tensión visible en el resto del equipo durante las reuniones.", opts: [
    { txt: "Intervengo en su próxima reunión para dictaminar cómo deben trabajar, evitando así que la discusión escale.", pts: 1, l: "A" },
    { txt: "Les envío un correo recordando las normas de convivencia de la unidad, pidiendo que resuelvan sus diferencias.", pts: 0, l: "B" },
    { txt: "Cito a ambos a una reunión para exponer los hechos que he observado, facilitando que construyan un acuerdo.", pts: 3, l: "C" },
    { txt: "Me reúno con cada uno por separado para escuchar su versión del problema y luego decido la mejor forma de actuar.", pts: 2, l: "D" },
  ]},
  { id: "C4", dim: "D4", ctx: "Un funcionario en teletrabajo tarda varias horas en responder correos importantes y sus avances diarios son menores a los esperados durante la semana.", opts: [
    { txt: "Reviso la plataforma de tareas para comprobar su avance real antes de asumir inactividad por falta de respuesta.", pts: 2, l: "A" },
    { txt: "Le exijo que mantenga un canal de comunicación abierto y me notifique cada vez que se aleje del computador.", pts: 1, l: "B" },
    { txt: "Suspendo su modalidad de teletrabajo de forma temporal hasta asegurar que cumpla con los tiempos esperados.", pts: 0, l: "C" },
    { txt: "Agendo una llamada para revisar juntos sus metas de la semana y acordar expectativas sobre tiempos de respuesta.", pts: 3, l: "D" },
  ]},
  { id: "C5", dim: "D5", ctx: "A mitad de mes, la Dirección Nacional introduce una nueva prioridad estratégica que altera significativamente la planificación acordada con su unidad.", opts: [
    { txt: "Evalúo el impacto de la nueva prioridad, ajusto las cargas de trabajo del equipo y comunico el cambio de enfoque.", pts: 3, l: "A" },
    { txt: "Asigno la nueva prioridad a las personas con mayor capacidad de respuesta, manteniendo al resto igual.", pts: 2, l: "B" },
    { txt: "Traslado la instrucción al equipo inmediatamente, pidiéndoles un esfuerzo adicional para cumplir con todo.", pts: 1, l: "C" },
    { txt: "Ejecuto la nueva prioridad personalmente para no interrumpir el flujo del equipo y asegurar los planes originales.", pts: 0, l: "D" },
  ]},
  { id: "C6", dim: "D6", ctx: "Un integrante experimentado ha bajado notoriamente la calidad de sus entregas en las últimas semanas, cometiendo errores básicos que antes no ocurrían.", opts: [
    { txt: "Le asigno un compañero como revisor de su trabajo para evitar errores graves que afecten la calidad final.", pts: 1, l: "A" },
    { txt: "Organizo una instancia de retroalimentación para indagar causas y basándome en ejemplos acordar mejoras.", pts: 3, l: "B" },
    { txt: "Derivo las tareas complejas a otro funcionario temporalmente, esperando que su desempeño vuelva a la normalidad.", pts: 0, l: "C" },
    { txt: "Le señalo directamente en la reunión de equipo la importancia de mantener los estándares institucionales esperados.", pts: 2, l: "D" },
  ]},
  { id: "C7", dim: "D1", ctx: "Acaba de ascender a una persona de su equipo al rol de jefe de proyecto. Usted nota que esta persona está revisando cada detalle (micromanagement) y frustrando al resto.", opts: [
    { txt: "Sugiero al coordinador que delege más tareas, recordándole que su rol ahora es supervisar y no ejecutar.", pts: 2, l: "A" },
    { txt: "Intervengo directamente con el sub-equipo para asegurarles que tendrán la autonomía necesaria para trabajar.", pts: 1, l: "B" },
    { txt: "Analizo con el coordinador cómo está distribuyendo el trabajo, ayudándole a identificar qué puede delegar hoy.", pts: 3, l: "C" },
    { txt: "Le solicito al coordinador que me copie en todos los correos que envíe a su equipo para monitorear su estilo.", pts: 0, l: "D" },
  ]},
  { id: "C8", dim: "D3", ctx: "Usted recibe una fuerte crítica formal de otra jefatura de ANID, acusando que un proceso gestionado por su equipo tiene fallas graves que afectan sus propios indicadores.", opts: [
    { txt: "Rechazo la crítica argumentando que mi equipo siempre sigue los procedimientos y pido que revisen sus procesos.", pts: 0, l: "A" },
    { txt: "Transmito la crítica inmediatamente al equipo para que corrijan errores, advirtiendo que nuestra reputación peligra.", pts: 1, l: "B" },
    { txt: "Analizo la situación internamente con mi equipo para construir una defensa sólida antes de responder formalmente.", pts: 2, l: "C" },
    { txt: "Solicito una reunión con la otra jefatura para comprender en detalle el impacto de los errores y explorar soluciones.", pts: 3, l: "D" },
  ]},
];

const LIKERT_LABELS = { 1: "Nunca", 2: "Pocas veces", 3: "A veces", 4: "Frecuentemente", 5: "Casi siempre" };

// Sistema de color por dimensión (D1-D6). Es SOLO presentación: ayuda a la
// orientación visual (pestañas, encabezado, numeración, franja lateral) y no
// altera preguntas, puntajes ni lógica del instrumento.
const DIM_COLORS = {
  D1: { icon: UserCog, solid: "bg-blue-600", solidHover: "hover:bg-blue-700", borderColor: "border-blue-500", tint: "bg-blue-50", tintBorder: "border-blue-200", tintText: "text-blue-700", ring: "focus-within:ring-blue-300/60", chipActive: "bg-blue-600 text-white border-blue-600" },
  D2: { icon: BarChart3, solid: "bg-emerald-600", solidHover: "hover:bg-emerald-700", borderColor: "border-emerald-500", tint: "bg-emerald-50", tintBorder: "border-emerald-200", tintText: "text-emerald-700", ring: "focus-within:ring-emerald-300/60", chipActive: "bg-emerald-600 text-white border-emerald-600" },
  D3: { icon: Users, solid: "bg-violet-600", solidHover: "hover:bg-violet-700", borderColor: "border-violet-500", tint: "bg-violet-50", tintBorder: "border-violet-200", tintText: "text-violet-700", ring: "focus-within:ring-violet-300/60", chipActive: "bg-violet-600 text-white border-violet-600" },
  D4: { icon: Share2, solid: "bg-orange-600", solidHover: "hover:bg-orange-700", borderColor: "border-orange-500", tint: "bg-orange-50", tintBorder: "border-orange-200", tintText: "text-orange-700", ring: "focus-within:ring-orange-300/60", chipActive: "bg-orange-600 text-white border-orange-600" },
  D5: { icon: Target, solid: "bg-rose-600", solidHover: "hover:bg-rose-700", borderColor: "border-rose-500", tint: "bg-rose-50", tintBorder: "border-rose-200", tintText: "text-rose-700", ring: "focus-within:ring-rose-300/60", chipActive: "bg-rose-600 text-white border-rose-600" },
  D6: { icon: Lightbulb, solid: "bg-cyan-600", solidHover: "hover:bg-cyan-700", borderColor: "border-cyan-500", tint: "bg-cyan-50", tintBorder: "border-cyan-200", tintText: "text-cyan-700", ring: "focus-within:ring-cyan-300/60", chipActive: "bg-cyan-600 text-white border-cyan-600" },
};

const STEP_ORDER = [
  { key: "general", label: "Datos generales" },
  { key: "likert", label: "Frecuencia de conductas" },
  { key: "cases", label: "Casos" },
];

function StepIndicator({ phase }) {
  const idx = STEP_ORDER.findIndex((s) => s.key === phase);
  return (
    <div className="flex items-center" data-testid="diagnostic-step-indicator">
      {STEP_ORDER.map((s, i) => {
        const done = idx > i || phase === "report";
        const active = idx === i;
        return (
          <div key={s.key} className="flex items-center">
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 shrink-0 transition-colors ${done ? "bg-emerald-500 border-emerald-500 text-white" : active ? "bg-brand-accent border-brand-accent text-white" : "bg-white border-slate-300 text-slate-400"}`}>
                {done ? <Check className="w-3 h-3" /> : i + 1}
              </div>
              <span className={`text-[11px] font-bold hidden sm:inline whitespace-nowrap ${active ? "text-slate-800" : done ? "text-slate-500" : "text-slate-400"}`}>{s.label}</span>
            </div>
            {i < STEP_ORDER.length - 1 && <div className={`w-5 sm:w-10 h-[2px] mx-2 rounded ${done ? "bg-emerald-400" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function semaphoreColor(sem) {
  return sem === "verde" ? "#10B981" : sem === "amarillo" ? "#F59E0B" : "#EF4444";
}

// ---------------------------------------------------------------------------
// BUG CRÍTICO DE PRIVACIDAD (en corrección): se detectó que, en ciertas
// condiciones de red/caché compartida, la descarga del informe PDF podía
// mostrar datos de otra persona. Mientras se termina de verificar la
// corrección (headers no-store + verificación de propiedad en backend), la
// DESCARGA del PDF queda deshabilitada por precaución. El diagnóstico, el
// guardado de respuestas y la visualización del informe EN PANTALLA (que
// siempre consulta en vivo al backend con la sesión del participante actual)
// siguen funcionando normalmente.
const DIAGNOSTIC_PDF_DOWNLOAD_ENABLED = true;

export default function DiagnosticPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading");
  const [curDimIdx, setCurDimIdx] = useState(0);
  const [curCaseIdx, setCurCaseIdx] = useState(0);
  const [likert, setLikert] = useState({});
  const [cases, setCases] = useState({});
  const [general, setGeneral] = useState({ years_in_role: "1 a 3 años", team_size: "1-5 personas" });
  const [report, setReport] = useState(null);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [saveTick, setSaveTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/participant/diagnostic");
        if (data.exists && data.status === "completed") {
          setReport(data.results);
          setPhase("report");
        } else if (data.exists) {
          setGeneral(data.general || general);
          setLikert(data.likert_responses || {});
          setCases(data.case_responses || {});
          setPhase(Object.keys(data.likert_responses || {}).length >= 18 ? "cases" : "likert");
        } else {
          setPhase("general");
        }
      } catch {
        setPhase("general");
      }
    })();
    // SEGURIDAD/PRIVACIDAD: al desmontar esta pantalla (ej. volver al portal
    // o cerrar sesión), se limpia explícitamente el informe/respuestas en
    // memoria para que ningún dato del diagnóstico anterior quede disponible
    // si otra persona vuelve a entrar en el mismo dispositivo/pestaña.
    return () => {
      setReport(null);
      setLikert({});
      setCases({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const startDiagnostic = async (e) => {
    e.preventDefault();
    try {
      await api.post("/participant/diagnostic/start", general);
      setPhase("likert");
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  // IMPORTANTE: cada respuesta se guarda de forma INDEPENDIENTE E INMEDIATA (sin
  // debounce compartido entre preguntas). Cada pregunta tiene su propio
  // AbortController: si el usuario cambia dos veces la MISMA pregunta muy rápido,
  // se cancela solo la petición anterior de ESA pregunta, pero nunca afecta a
  // otras preguntas. El backend además aplica los cambios de forma atómica por
  // campo (notación de punto de Mongo), así que ninguna respuesta anterior puede
  // ser sobrescrita ni eliminada por clics rápidos.
  const likertAbort = useRef({});
  const casesAbort = useRef({});

  const saveLikert = (qId, v) => {
    const next = { ...likert, [qId]: v };
    setLikert(next);
    if (likertAbort.current[qId]) likertAbort.current[qId].abort();
    const controller = new AbortController();
    likertAbort.current[qId] = controller;
    api.put("/participant/diagnostic/save", { likert_responses: { [qId]: v } }, { signal: controller.signal })
      .then(() => setSaveTick((t) => t + 1))
      .catch(() => {});
  };

  const saveCase = (cId, pts) => {
    const next = { ...cases, [cId]: pts };
    setCases(next);
    if (casesAbort.current[cId]) casesAbort.current[cId].abort();
    const controller = new AbortController();
    casesAbort.current[cId] = controller;
    api.put("/participant/diagnostic/save", { case_responses: { [cId]: pts } }, { signal: controller.signal })
      .then(() => setSaveTick((t) => t + 1))
      .catch(() => {});
  };

  // Flush de seguridad: al avanzar de fase, reenvía el estado completo actual
  // (idempotente y atómico por campo en el backend) para garantizar consistencia
  // incluso si alguna llamada individual falló por red.
  const resyncAll = async () => {
    const payload = {};
    if (Object.keys(likert).length) payload.likert_responses = { ...likert };
    if (Object.keys(cases).length) payload.case_responses = { ...cases };
    if (Object.keys(payload).length === 0) return;
    try {
      await api.put("/participant/diagnostic/save", payload);
    } catch {
      // se mantiene en estado local; el usuario puede reintentar
    }
  };

  const changeDim = (dir) => {
    if (curDimIdx + dir >= 0 && curDimIdx + dir < 6) { setCurDimIdx(curDimIdx + dir); window.scrollTo({ top: 100, behavior: "smooth" }); }
  };
  const changeCase = (dir) => {
    if (curCaseIdx + dir >= 0 && curCaseIdx + dir < 8) { setCurCaseIdx(curCaseIdx + dir); window.scrollTo({ top: 150, behavior: "smooth" }); }
  };

  const goToCases = async () => {
    await resyncAll();
    if (Object.keys(likert).length < 18) { showToast("Debe completar las 18 conductas para avanzar a los Casos."); return; }
    setPhase("cases");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitFinal = async () => {
    await resyncAll();
    if (Object.keys(cases).length < 8) { showToast("Responda los 8 casos para finalizar."); return; }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/participant/diagnostic/submit");
      setReport(data.results);
      setPhase("report");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get("/participant/diagnostic/report.pdf", { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = match ? match[1] : "Informe_Diagnostico_ANID_INSIDE.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showToast("No fue posible descargar el informe. Intenta nuevamente.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (phase === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 text-brand-accent animate-spin" /></div>;
  }

  return (
    <div className="min-h-full flex flex-col bg-[color:var(--brand-bg)] text-slate-800 font-sans">
      <header className="bg-[color:var(--brand-navy)] no-print" data-testid="diagnostic-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="bg-white/95 border border-white/10 rounded-lg px-2 py-1.5 shrink-0">
              <img src="/brand/inside-logo.png" alt="INSIDE" className="h-[18px] w-auto" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">ANID · Programa de Formación y Desarrollo de Competencias de Liderazgo</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-testid="diagnostic-title">
            Diagnóstico Inicial de Competencias de Liderazgo
          </h1>
          <p className="text-sm text-white/60 mt-1.5 max-w-2xl">Este diagnóstico permitirá identificar fortalezas y oportunidades de desarrollo para orientar los contenidos y actividades del curso.</p>
        </div>
      </header>

      <nav className="bg-white border-b border-[color:var(--brand-border)] shadow-sm sticky top-0 z-40 no-print">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <button onClick={() => navigate("/portal")} data-testid="diagnostic-back-to-portal" className="text-xs font-bold text-slate-500 hover:text-brand-accent flex items-center gap-1 transition-colors shrink-0">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Portal</span>
          </button>
          {phase !== "report" && <StepIndicator phase={phase} />}
        </div>
      </nav>

      <main className="flex-grow max-w-5xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {toast && <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-5 py-3.5 rounded-xl shadow-2xl text-xs sm:text-sm font-medium no-print">{toast}</div>}
        {error && <div className="mb-4 text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">{error}</div>}

        {phase === "general" && (
          <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04),0_12px_24px_rgba(15,23,42,0.06)] p-6 sm:p-9">
            <div className="mb-6 pb-4 border-b border-slate-200">
              <span className="text-xs font-bold uppercase tracking-wider text-brand-accent">Paso 1 de 3</span>
              <h2 className="font-display text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">Datos generales</h2>
              <p className="text-xs text-slate-500 mt-1.5">Esta información ayuda a contextualizar tus resultados. No modifica el cálculo del diagnóstico.</p>
            </div>
            <form onSubmit={startDiagnostic} className="space-y-5" data-testid="diagnostic-general-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">Tiempo en jefatura</label>
                  <select data-testid="diagnostic-years-select" value={general.years_in_role} onChange={(e) => setGeneral({ ...general, years_in_role: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-accent/20 focus:border-brand-accent transition-shadow">
                    <option>Menos de 1 año</option>
                    <option>1 a 3 años</option>
                    <option>4 a 6 años</option>
                    <option>Más de 6 años</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">Personas en su equipo</label>
                  <select data-testid="diagnostic-teamsize-select" value={general.team_size} onChange={(e) => setGeneral({ ...general, team_size: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-accent/20 focus:border-brand-accent transition-shadow">
                    <option value="1-5 personas">1 a 5 personas</option>
                    <option value="6-10 personas">6 a 10 personas</option>
                    <option value="11 o más personas">11 o más personas</option>
                  </select>
                </div>
              </div>
              <div className="pt-4 flex justify-end">
                <button data-testid="diagnostic-start-btn" type="submit" className="h-11 px-8 bg-brand-accent hover:bg-brand-accentHover text-white font-bold rounded-xl shadow-md flex items-center gap-2 text-sm transition-colors active:translate-y-[1px]">
                  Comenzar evaluación <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        )}

        {phase === "likert" && (() => {
          const d = SURVEY_DIMENSIONS[curDimIdx];
          const c = DIM_COLORS[d.id];
          const isLast = curDimIdx === 5;
          return (
            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04),0_12px_24px_rgba(15,23,42,0.06)] p-5 sm:p-8">
              <div className="mb-5 pb-4 border-b border-slate-200 flex justify-between items-center gap-4">
                <div>
                  <span className="text-xs font-bold uppercase text-brand-accent">Paso 2 de 3</span>
                  <h2 className="font-display text-xl sm:text-2xl font-extrabold text-slate-900 mt-0.5">Frecuencia de conductas</h2>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <AutosaveBanner trigger={saveTick} />
                  <div className="text-xs font-bold text-slate-500 text-right">
                    Progreso: <span data-testid="diagnostic-progress-text" className="text-brand-slate font-extrabold text-sm ml-1">{Object.keys(likert).length} / 18</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 pb-3 mb-6 border-b border-[color:var(--brand-border)] sticky top-[49px] sm:top-[53px] z-30 bg-white pt-1" data-testid="diagnostic-dimension-tabs">
                {SURVEY_DIMENSIONS.map((dim, i) => {
                  const dc = DIM_COLORS[dim.id];
                  const complete = dim.questions.every((q) => likert[q.id]);
                  const active = i === curDimIdx;
                  const cls = active ? dc.chipActive : complete ? `${dc.tint} ${dc.tintText} ${dc.tintBorder}` : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50";
                  return (
                    <button key={dim.id} type="button" onClick={() => setCurDimIdx(i)} data-testid={`diagnostic-tab-${dim.id}`} className={`h-11 rounded-xl text-xs font-black border-2 transition-colors flex items-center justify-center gap-1.5 ${cls}`}>
                      {complete && !active ? <Check className="w-3.5 h-3.5" /> : null}
                      {dim.code}
                    </button>
                  );
                })}
              </div>

              <div className={`p-5 rounded-2xl mb-6 border-l-4 ${c.tint} ${c.borderColor}`}>
                <div className="flex items-center gap-3 mb-1">
                  <span className={`w-9 h-9 rounded-full ${c.solid} text-white flex items-center justify-center shrink-0`}><c.icon className="w-4 h-4" /></span>
                  <div>
                    <span className={`text-[10px] font-black uppercase tracking-wider ${c.tintText}`}>Dimensión {d.code.slice(1)}</span>
                    <h3 className="font-display text-sm sm:text-base font-bold text-slate-900">{d.title.split(". ")[1]}</h3>
                  </div>
                </div>
                <p className="text-xs text-slate-600 mt-1 ml-12">{d.desc}</p>
              </div>

              <div className="space-y-4">
                {d.questions.map((q, qi) => {
                  const val = likert[q.id];
                  return (
                    <div key={q.id} className={`p-5 rounded-2xl border-l-4 border transition-colors ${val ? `${c.tint} ${c.borderColor}` : "border-l-slate-200 border-slate-200 bg-white"}`}>
                      <p className="text-sm font-semibold text-slate-800 mb-4"><span className={`${c.tintText} font-bold mr-1.5`}>{qi + 1}.</span>{q.text}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center" role="radiogroup" aria-label={q.text}>
                        {[1, 2, 3, 4, 5].map((v) => {
                          const selected = val === v;
                          return (
                            <label key={v} className="cursor-pointer" data-testid={`likert-${q.id}-${v}`}>
                              <input type="radio" name={q.id} value={v} checked={selected} onChange={() => saveLikert(q.id, v)} className="sr-only peer" />
                              <div className={`relative py-2.5 px-1 rounded-xl border-2 transition-colors flex flex-col items-center focus-within:ring-4 ${c.ring} ${selected ? `${c.solid} ${c.solidHover} border-transparent text-white shadow-md` : `border-slate-200 bg-white text-slate-600 hover:${c.tint} hover:${c.tintBorder}`}`}>
                                {selected && <CheckCircle2 className="w-3.5 h-3.5 absolute top-1 right-1 text-white/90" />}
                                <span className="text-xs font-black">{v}</span>
                                <span className="text-[9px] font-bold mt-0.5 leading-tight">{LIKERT_LABELS[v]}</span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
                <button disabled={curDimIdx === 0} onClick={() => changeDim(-1)} className="h-11 px-5 border border-slate-300 rounded-xl font-bold text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none flex items-center gap-1 transition-colors"><ArrowLeft className="w-4 h-4" /> Anterior</button>
                {!isLast ? (
                  <button onClick={() => changeDim(1)} className="h-11 px-6 bg-brand-slate text-white rounded-xl font-bold text-sm hover:bg-slate-900 shadow flex items-center gap-1 transition-colors">Siguiente <ArrowRight className="w-4 h-4" /></button>
                ) : (
                  <button data-testid="diagnostic-goto-cases-btn" onClick={goToCases} className="h-12 px-8 bg-brand-accent hover:bg-brand-accentHover text-white rounded-xl font-black text-sm shadow-md flex items-center gap-2 transition-colors">Siguiente fase: Casos <ArrowRight className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          );
        })()}

        {phase === "cases" && (() => {
          const cs = CASES_DATA[curCaseIdx];
          const dimMeta = SURVEY_DIMENSIONS.find((d) => d.id === cs.dim);
          const c = DIM_COLORS[cs.dim];
          const selectedPts = cases[cs.id];
          const isLast = curCaseIdx === 7;
          return (
            <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04),0_12px_24px_rgba(15,23,42,0.06)] p-5 sm:p-8">
              <div className="mb-5 pb-4 border-b border-slate-200 flex justify-between items-center gap-4">
                <div>
                  <span className="text-xs font-bold uppercase text-brand-accent">Paso 3 de 3</span>
                  <h2 className="font-display text-xl sm:text-2xl font-extrabold text-slate-900 mt-0.5">Resolución de casos</h2>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <AutosaveBanner trigger={saveTick} />
                  <div className="text-xs font-bold text-slate-500">Caso <span className="text-brand-slate font-extrabold text-sm mx-1">{curCaseIdx + 1}</span> de 8</div>
                </div>
              </div>

              <blockquote className="border-l-4 border-brand-accent bg-[color:var(--brand-sky-tint)] rounded-r-xl px-4 py-3 mb-6 italic text-xs sm:text-sm text-slate-600">
                “El liderazgo es una oportunidad para generar un impacto positivo en las personas y en la sociedad.”
              </blockquote>

              <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl mb-6 flex items-start gap-2.5">
                <Scale className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-sm font-bold text-amber-900 mb-1">Instrucciones</h3>
                  <p className="text-xs text-amber-800">Lea la situación descrita y seleccione la opción que represente de mejor manera su <strong>primera acción o decisión principal</strong> frente al escenario.</p>
                </div>
              </div>

              {/* Caja del CASO — claramente diferenciada de las alternativas */}
              <div className={`relative rounded-2xl p-5 sm:p-6 mb-6 border-2 ${c.tintBorder} ${c.tint} overflow-hidden`} data-testid="case-context-box">
                <div className={`absolute top-0 left-0 right-0 h-1.5 ${c.solid}`} />
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-8 h-8 rounded-full ${c.solid} text-white flex items-center justify-center shrink-0`}><c.icon className="w-3.5 h-3.5" /></span>
                  <span className={`text-[10px] font-black uppercase tracking-wider ${c.tintText} bg-white/70 border ${c.tintBorder} px-2.5 py-1 rounded-full`}>
                    Competencia relacionada: {dimMeta.title.split(".")[1]}
                  </span>
                </div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Situación</p>
                <p className="text-sm sm:text-[15px] font-semibold text-slate-800 leading-relaxed">{cs.ctx}</p>
                <div className={`mt-4 flex items-start gap-2 bg-white/70 border ${c.tintBorder} rounded-xl px-3.5 py-2.5`}>
                  <MessageCircleQuestion className={`w-4 h-4 ${c.tintText} mt-0.5 shrink-0`} />
                  <p className={`text-xs sm:text-sm italic font-semibold ${c.tintText}`}>¿Cómo abordarías esta situación?</p>
                </div>
              </div>

              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Alternativas de respuesta</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {cs.opts.map((opt) => {
                  const selected = selectedPts === opt.pts;
                  return (
                    <label key={opt.l} className="cursor-pointer block" data-testid={`case-${cs.id}-${opt.l}`}>
                      <input type="radio" name={cs.id} checked={selected} onChange={() => saveCase(cs.id, opt.pts)} className="sr-only" />
                      <div className={`h-full p-4 rounded-xl border-2 transition-colors flex items-start gap-3 ${selected ? `${c.tint} ${c.borderColor}` : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 hover:-translate-y-0.5"}`}>
                        <span className={`w-7 h-7 rounded-full font-black text-xs flex items-center justify-center shrink-0 border-2 ${selected ? `${c.solid} text-white border-transparent` : "bg-slate-50 text-slate-500 border-slate-200"}`}>{opt.l}</span>
                        <span className="text-sm font-medium text-slate-700 mt-0.5 flex-1">{opt.txt}</span>
                        {selected && <CheckCircle2 className={`w-5 h-5 shrink-0 ${c.tintText}`} />}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
                <button disabled={curCaseIdx === 0} onClick={() => changeCase(-1)} className="h-11 px-5 border border-slate-300 rounded-xl font-bold text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none flex items-center gap-1 transition-colors"><ArrowLeft className="w-4 h-4" /> Anterior</button>
                {!isLast ? (
                  <button onClick={() => changeCase(1)} className="h-11 px-6 bg-brand-slate text-white rounded-xl font-bold text-sm hover:bg-slate-900 shadow flex items-center gap-1 transition-colors">Siguiente caso <ArrowRight className="w-4 h-4" /></button>
                ) : (
                  <button data-testid="diagnostic-submit-btn" disabled={submitting} onClick={submitFinal} className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-sm shadow-md flex items-center gap-2 transition-colors disabled:opacity-60">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Finalizar y ver informe
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {phase === "report" && report && <ReportView report={report} onBack={() => navigate("/portal")} onDownloadPdf={downloadPdf} downloading={downloadingPdf} />}
      </main>

      <footer className="bg-[color:var(--brand-navy)] text-white px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 no-print mt-auto">
        <p className="text-[10px] font-semibold text-slate-300">Herramienta de apoyo al programa desarrollada por INSIDE® Training &amp; Learning</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Diagnóstico formativo · No constituye evaluación de desempeño</p>
      </footer>
    </div>
  );
}

const LEVEL_BADGE_CLS = {
  verde: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amarillo: "bg-amber-50 text-amber-800 border-amber-200",
  rojo: "bg-rose-50 text-rose-700 border-rose-200",
};

function ReportView({ report, onBack, onDownloadPdf, downloading }) {
  const dims = report.dimensions;
  const radarData = dims.map((d) => ({ code: d.code, score: d.score, full: 100 }));
  const barData = dims.map((d) => ({ code: d.code, score: d.score, fill: semaphoreColor(d.semaphore) }));
  const legend = report.legend || [
    { semaphore: "verde", range: "75–100", label: "Fortaleza relativa" },
    { semaphore: "amarillo", range: "60–74", label: "Desarrollo intermedio" },
    { semaphore: "rojo", range: "0–59", label: "Área a fortalecer" },
  ];
  const devAreas = report.development_areas || [];
  const dimLevels = report.dimension_levels || {};

  const hBadge = report.ihd_semaphore === "verde" ? { label: report.ihd_level || "Fortaleza relativa", cls: "text-emerald-600" }
    : report.ihd_semaphore === "amarillo" ? { label: report.ihd_level || "Desarrollo intermedio", cls: "text-amber-500" }
    : { label: report.ihd_level || "Área a fortalecer", cls: "text-rose-600" };

  return (
    <div className="space-y-6 print-full">
      <div className="flex items-center justify-between no-print">
        <button onClick={onBack} className="text-xs font-bold text-slate-500 hover:text-brand-accent flex items-center gap-1 transition-colors"><ArrowLeft className="w-4 h-4" /> Volver al portal</button>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
          <CheckCircle2 className="w-3.5 h-3.5" /> Diagnóstico registrado correctamente
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[color:var(--brand-border)] p-6 shadow-[var(--shadow-sm)] avoid-break" data-testid="report-kpi-habitos-card">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-[color:var(--text-primary)] tracking-tight">Informe de resultados</h2>
            <p className="text-xs text-[color:var(--text-secondary)] mt-1">Versión del instrumento: {report.instrument_version}</p>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <div className="bg-[var(--surface-2)] border border-[color:var(--brand-border)] p-4 rounded-xl flex-1 md:w-56" data-testid="report-kpi-habitos">
              <span className="text-[9px] font-bold text-[color:var(--text-secondary)] uppercase block">Índice Hábitos Directivos</span>
              <div className="flex items-baseline gap-1.5 mt-1.5">
                <span className="text-4xl font-black text-[color:var(--text-primary)] tabular-nums leading-none">{report.ihd}</span>
                <span className="text-sm text-slate-400 font-bold">/100</span>
              </div>
              <span className={`text-[10px] font-black uppercase block mt-1.5 ${hBadge.cls}`}>{hBadge.label}</span>
            </div>
            <div className="bg-[color:var(--surface-inset)] border border-[color:var(--brand-border)] p-4 rounded-xl flex-1 md:w-56" data-testid="report-kpi-situacional">
              <span className="text-[9px] font-bold text-[color:var(--text-secondary)] uppercase block">Criterio Situacional</span>
              <div className="flex items-baseline gap-1.5 mt-1.5">
                <span className="text-4xl font-black text-brand-accent tabular-nums leading-none">{report.cases_total_24}</span>
                <span className="text-sm text-slate-400 font-bold">/24</span>
              </div>
              <span className="text-[10px] font-black uppercase text-brand-accent block mt-1.5">Resolución de casos</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-[color:var(--brand-border)]">
          {legend.map((item) => (
            <span key={item.semaphore} className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border ${LEVEL_BADGE_CLS[item.semaphore]}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: semaphoreColor(item.semaphore) }} /> {item.range}: {item.label}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 avoid-break">
        <div className="bg-white rounded-2xl p-5 border border-[color:var(--brand-border)]" data-testid="report-radar-chart">
          <h3 className="text-xs font-bold text-[color:var(--text-secondary)] uppercase text-center border-b border-[color:var(--brand-border)] pb-2 mb-4">Perfil radar</h3>
          <div className="relative h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="code" tick={{ fontSize: 12, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
                <Radar dataKey="score" stroke="#2563EB" fill="#2563EB" fillOpacity={0.18} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-[color:var(--brand-border)]" data-testid="report-bar-chart">
          <h3 className="text-xs font-bold text-[color:var(--text-secondary)] uppercase text-center border-b border-[color:var(--brand-border)] pb-2 mb-4">Puntaje por dimensión (0-100)</h3>
          <div className="relative h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="code" tick={{ fontSize: 12, fontWeight: 700 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                  {barData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-[color:var(--brand-border)] avoid-break">
        <h4 className="font-display text-sm font-black text-[color:var(--text-primary)] uppercase border-b border-[color:var(--brand-border)] pb-2 mb-4">Perfil de competencias</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dims.map((d) => {
            const meta = SURVEY_DIMENSIONS.find((sd) => sd.id === d.id);
            const dc = DIM_COLORS[d.id];
            return (
              <div key={d.id} className={`flex items-start gap-3 p-3.5 rounded-xl border ${dc.tint} ${dc.tintBorder}`}>
                <div className={`w-8 h-8 rounded-lg ${dc.solid} text-white font-black text-xs flex items-center justify-center shrink-0`}>{d.code}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h5 className="text-xs font-bold text-[color:var(--text-primary)]">{d.title}</h5>
                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded border shrink-0 ${LEVEL_BADGE_CLS[d.semaphore]}`}>{d.score}</span>
                  </div>
                  <p className="text-[10px] text-[color:var(--text-secondary)] leading-tight mt-0.5">{meta?.desc}</p>
                  <p className="text-[10px] font-bold mt-1" style={{ color: semaphoreColor(d.semaphore) }}>{dimLevels[d.id]}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {devAreas.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-[color:var(--brand-border)] avoid-break" data-testid="report-development-areas">
          <h4 className="font-display text-sm font-black text-[color:var(--text-primary)] uppercase border-b border-[color:var(--brand-border)] pb-2 mb-4">Áreas sugeridas de profundización</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {devAreas.map((a, idx) => {
              const dc = DIM_COLORS[a.id] || DIM_COLORS.D1;
              return (
                <div key={a.id} className={`relative rounded-xl border ${dc.tintBorder} ${dc.tint} p-4 pt-9`}>
                  <span className={`absolute top-3 left-4 text-[10px] font-black ${dc.tintText} opacity-70`}>0{idx + 1}</span>
                  <span className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${dc.solid} text-white mb-2`}>{a.code}</span>
                  <p className="text-xs font-semibold text-[color:var(--text-primary)] leading-relaxed">{a.text}</p>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-[color:var(--text-secondary)] mt-4">Los resultados tienen carácter formativo y no constituyen una evaluación de desempeño laboral.</p>
        </div>
      )}

      <div className="bg-white rounded-2xl p-5 border border-[color:var(--brand-border)] flex flex-col sm:flex-row justify-between items-center gap-4 no-print">
        <p className="text-sm font-bold text-[color:var(--text-secondary)]">Resultados guardados en la base de datos del programa.</p>
        {DIAGNOSTIC_PDF_DOWNLOAD_ENABLED ? (
          <button data-testid="report-download-pdf-btn" onClick={onDownloadPdf} disabled={downloading} className="h-11 px-8 bg-brand-accent hover:bg-brand-accentHover text-white font-extrabold rounded-xl shadow-md text-xs uppercase flex items-center gap-2 transition-colors disabled:opacity-60">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Descargar informe PDF
          </button>
        ) : (
          <div className="flex flex-col items-end gap-1.5">
            <button data-testid="report-download-pdf-btn" disabled title="Descarga temporalmente deshabilitada por mantenimiento de seguridad" className="h-11 px-8 bg-slate-200 text-slate-400 font-extrabold rounded-xl text-xs uppercase flex items-center gap-2 cursor-not-allowed">
              <FileDown className="w-4 h-4" /> Descargar informe PDF
            </button>
            <p className="text-[10px] text-slate-400 max-w-xs text-right">Descarga temporalmente deshabilitada por mantenimiento de seguridad. Puedes seguir revisando tu informe en esta pantalla.</p>
          </div>
        )}
      </div>
    </div>
  );
}
